import { useAuth } from '../contexts/AuthContext';

// Feature permission constants
export const PERMISSIONS = {
  // Dashboard permissions
  DASHBOARD_FULL: 'DASHBOARD_FULL',
  DASHBOARD_LIMITED: 'DASHBOARD_LIMITED',
  
  // Order permissions
  ORDERS_FULL: 'ORDERS_FULL',
  ORDERS_VIEW: 'ORDERS_VIEW',
  ORDERS_UPDATE: 'ORDERS_UPDATE',
  
  // Inventory permissions
  INVENTORY_FULL: 'INVENTORY_FULL',
  INVENTORY_VIEW: 'INVENTORY_VIEW',
  
  // Customer permissions
  CUSTOMERS_FULL: 'CUSTOMERS_FULL',
  CUSTOMERS_VIEW: 'CUSTOMERS_VIEW',
  
  // Payment permissions
  PAYMENTS_FULL: 'PAYMENTS_FULL',
  
  // History permissions
  HISTORY_FULL: 'HISTORY_FULL',
  HISTORY_LIMITED: 'HISTORY_LIMITED',
  
  // Admin permissions (owner only)
  USER_MANAGEMENT: 'USER_MANAGEMENT',
  SETTINGS_FULL: 'SETTINGS_FULL',
  FEATURES_FULL: 'FEATURES_FULL',
  ANALYTICS_FULL: 'ANALYTICS_FULL',
} as const;

// Default permissions for different roles
export const DEFAULT_PERMISSIONS = {
  owner: Object.values(PERMISSIONS),
  worker: [
    PERMISSIONS.DASHBOARD_LIMITED,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.ORDERS_UPDATE,
  ] as string[],
};

export const usePermissions = () => {
  const { userData } = useAuth();
  
  const hasPermission = (permission: string): boolean => {
    console.log(`Checking permission ${permission} for user:`, userData);
    
    // Owners have all permissions
    if (userData?.role === 'owner') {
      console.log('User is owner, granting access');
      return true;
    }
    
    // Check if user has specific permission
    const hasAccess = userData?.permissions?.includes(permission) || false;
    console.log(`User has permission ${permission}:`, hasAccess);
    return hasAccess;
  };
  
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };
  
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };
  
  const isOwner = (): boolean => {
    return userData?.role === 'owner';
  };
  
  const isWorker = (): boolean => {
    return userData?.role === 'worker';
  };
  
  const getRole = (): string | null => {
    return userData?.role || null;
  };
  
  const getPermissions = (): string[] => {
    if (userData?.role === 'owner') {
      return Object.values(PERMISSIONS);
    }
    return userData?.permissions || [];
  };
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isOwner,
    isWorker,
    getRole,
    getPermissions,
    PERMISSIONS,
  };
}; 